import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger } from '@saviynt/common';
import {
  ButtonIcon,
  Icon,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import {
  CONSULT_MODAL_DETAILS_MODEL,
  NODE_DATA_MODEL,
} from '../../../models/CertsModels';
import { getPredefinedComments } from '../../../redux/certdetails/action';
import {
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { getMessageKey } from '../../../utils/utils';
import { isEmptyObject } from '../CertsReassignModal/utils';
import { PREDEFINED_COMMENTS_FOR_ACTION } from '../constant';

import CertsConsultContent from './CertsConsultContent';
import CertsConsultFooter from './CertsConsultFooter';

import './CertsConsultModal.css';

// TODO: Alert banner needs critical handler update for alert text and timing

function CertsConsultModal({
  isConsultModalOpen,
  setIsConsultModalOpen,
  setIsActionDrawerOutsideClick,
  consultModalDetails,
  nodeData,
  isCommentRequired,
  onSubmit,
  // Testing
  menuDropdownTestId,
  commentInputTestId,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  const [isOpen, setIsOpen] = useState(false);
  const [isCritical, setIsCritical] = useState(false);
  const [commentValue, setCommentValue] = useState('');
  const [isIncludeAssociateChecked, setIsIncludedAssociateChecked] =
    useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSelectVisible, setIsSelectVisible] = useState(false);

  const {
    core: { predefinedComments },
    global: { certKey, mediaQueryValue },
    lineActions: { isActionLoading, isActionSuccess },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const [commentsDetails, setCommentDetails] = useState({
    commentDescription: '',
    predefComments: [],
    isCommentApplied: false,
    showAlert: false,
  });

  const isCommentRequiredCompleteAndValid = Boolean(
    isCommentRequired &&
      commentValue &&
      !/[<>]/.test(commentValue) &&
      commentValue.trim().length >= 3
  );
  const isConsultValid =
    !isCritical &&
    selectedUser &&
    (!isCommentRequired || isCommentRequiredCompleteAndValid);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

  const dropdownClassName = classNames('CertsConsultModal-dropdown');

  useEffect(() => {
    setIsOpen(isConsultModalOpen);
  }, [isConsultModalOpen]);

  useEffect(() => {
    // by default checkbox should be selected for account
    if (consultModalDetails?.isIncludeAssociatedEnt)
      setIsIncludedAssociateChecked(true);
  }, [consultModalDetails]);

  useEffect(() => {
    if (isActionSuccess) {
      setIsConsultModalOpen(false);
      setIsActionDrawerOutsideClick(true);
    }
  }, [isActionSuccess]);

  useEffect(() => {
    if (commentValue && !isCommentRequiredCompleteAndValid)
      setIsCritical(false);
  }, [selectedUser, commentValue]);

  useEffect(() => {
    // to get predefined comments
    if (!isCommentRequired) return;

    if (PREDEFINED_COMMENTS_FOR_ACTION.CONSULT in predefinedComments) {
      setCommentDetails({
        ...commentsDetails,
        predefComments:
          predefinedComments[PREDEFINED_COMMENTS_FOR_ACTION.CONSULT] || [],
      });
    } else {
      dispatch(
        getPredefinedComments(PREDEFINED_COMMENTS_FOR_ACTION.CONSULT)
      ).catch((error) => Logger.log(error));
    }
  }, [predefinedComments]);

  const handleCommentDescription = (text) => {
    setCommentValue(text);
    setCommentDetails({
      ...commentsDetails,
      commentDescription: text,
      showAlert: text.length === 0 ? commentsDetails.showAlert : false,
    });
  };

  const handleSubmit = () => {
    const modalInfo = {
      isIncludeAssociateChecked,
      selectedUser,
      certKey,
    };

    if (!selectedUser) {
      setIsCritical(true);
      setIsSelectVisible(true);

      return;
    }

    if (!isCommentRequiredCompleteAndValid && isCommentRequired) {
      setIsCritical(true);
      setCommentDetails({
        ...commentsDetails,
        showAlert: true,
      });

      return;
    }

    if (isCommentRequired && commentsDetails?.commentDescription) {
      modalInfo.commentValue = commentsDetails.commentDescription;
    }

    onSubmit(modalInfo);
  };

  const handleSelectUser = (user) => {
    if (isEmptyObject(user)) {
      setSelectedUser(null);
      setIsSelectVisible(true);

      return;
    }

    setIsSelectVisible(false);

    setSelectedUser(user);
  };

  const consultContent = (
    <CertsConsultContent
      nodeData={nodeData}
      consultModalDetails={consultModalDetails}
      isIncludeAssociateChecked={isIncludeAssociateChecked}
      setIsIncludedAssociateChecked={setIsIncludedAssociateChecked}
      dropdownClassName={dropdownClassName}
      handleSelectUser={handleSelectUser}
      isSelectVisible={isSelectVisible}
      menuDropdownTestId={menuDropdownTestId}
      commentsDetails={commentsDetails}
      isCritical={isCritical}
      handleCommentDescription={handleCommentDescription}
      commentInputTestId={commentInputTestId}
      selectedUser={selectedUser}
      isCommentRequired={isCommentRequired}
      isMobileWidth={isMobileWidth}
    />
  );

  if (isMobileWidth) {
    return (
      <MobileDrawer
        className='CertsConsultModal-mobileDrawer'
        isOpen={isOpen}
        setIsOpen={() => {
          setIsConsultModalOpen(false);
          setIsActionDrawerOutsideClick(true);
        }}
        headerContent={
          <header className='CertsConsultModal-mobileDrawerHeader'>
            <Icon kind='Consult' color='neutral-1000' size='medium' />
            <div className='CertsConsultModal-mobileDrawerTitleContainer'>
              <Typography
                kind='h1'
                className='CertsConsultModal-mobileDrawerTitle'>
                {messages.CONSULT_WITH_PEERS ||
                  localize(intl, getMessageKey('CONSULT_WITH_PEERS'))}
              </Typography>
              <Typography
                kind='body2'
                className='CertsConsultModal-mobileDrawerSubTitle'>
                {messages.CONSULT_INVITE_REVIEW_LABEL ||
                  localize(intl, getMessageKey('CONSULT_INVITE_REVIEW_LABEL'))}
              </Typography>
            </div>
            <ButtonIcon
              size='medium'
              kind='ghost'
              icon={
                <Icon kind='close' color='neutral-100' size='smallMedium' />
              }
              onClick={() => {
                setIsConsultModalOpen(false);
                setIsActionDrawerOutsideClick(true);
              }}
            />
          </header>
        }
        content={consultContent}
        footerActionBar={
          <CertsConsultFooter
            isActionLoading={isActionLoading}
            setIsConsultModalOpen={setIsConsultModalOpen}
            isConsultValid={isConsultValid}
            handleSubmit={handleSubmit}
          />
        }
      />
    );
  }

  return (
    <ModalIsland
      isOverflowVisible={!isCommentRequired}
      onClose={() => {
        setIsOpen(false);
        setIsConsultModalOpen(false);
        setIsActionDrawerOutsideClick(true);
      }}
      className='CertsConsultModal'
      isOpen={isOpen}
      headerIcon={<Icon kind='Consult' color='neutral-1000' size='large' />}
      title={
        messages.CONSULT_WITH_PEERS ||
        localize(intl, getMessageKey('CONSULT_WITH_PEERS'))
      }
      subtitle={
        messages.CONSULT_INVITE_REVIEW_LABEL ||
        localize(intl, getMessageKey('CONSULT_INVITE_REVIEW_LABEL'))
      }
      contentBody={consultContent}
      FooterActionBarComp={
        <CertsConsultFooter
          isActionLoading={isActionLoading}
          setIsConsultModalOpen={setIsConsultModalOpen}
          isConsultValid={isConsultValid}
          handleSubmit={handleSubmit}
        />
      }
    />
  );
}

CertsConsultModal.propTypes = {
  isConsultModalOpen: PropTypes.bool.isRequired,
  setIsConsultModalOpen: PropTypes.func.isRequired,
  setIsActionDrawerOutsideClick: PropTypes.func,
  consultModalDetails: PropTypes.shape(CONSULT_MODAL_DETAILS_MODEL).isRequired,
  nodeData: PropTypes.shape(NODE_DATA_MODEL).isRequired,
  isCommentRequired: PropTypes.bool.isRequired,
  onSubmit: PropTypes.func.isRequired,
  menuDropdownTestId: PropTypes.string,
  commentInputTestId: PropTypes.string,
};

CertsConsultModal.defaultProps = {
  setIsActionDrawerOutsideClick: () => {},
  menuDropdownTestId: null,
  commentInputTestId: null,
};

export default CertsConsultModal;
